import { useMutation } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { accountApi } from '@/api/accountApi'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useSession } from '@/session/SessionContext'
import { showToast } from '@/theme/notifications'
import { OtpForm } from './OtpForm'

type OtpHandlerProps = {
  uid: string
  email: string
  resendKey?: string
}

export const OtpHandler = ({ uid, email, resendKey }: OtpHandlerProps) => {
  const intl = useIntl()
  const { refreshSession, isRefreshing } = useSession()

  const { mutateAsync: verifyOtp, isPending: isVerifying } = useMutation({
    mutationFn: accountApi.verifyOtp
  })

  const { mutateAsync: resendOtp, isPending: isResending } = useMutation({
    mutationFn: accountApi.resendOtp
  })

  const handleSubmit = async (otp: string) => {
    try {
      await verifyOtp({ uid, otp })
      await refreshSession()
      showToast(intl.formatMessage({ id: 'login.otp.success' }), 'success')
    } catch (err) {
      showToast(
        extractErrorMessage(err, intl.formatMessage({ id: 'login.otp.error' })),
        'error'
      )
    }
  }

  const handleResend = async () => {
    if (!resendKey) {
      return
    }

    try {
      await resendOtp({ uid, resend_key: resendKey })

      showToast(
        intl.formatMessage({ id: 'login.otp.resend.success' }),
        'success'
      )
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'login.otp.resend.error' })
        ),
        'error'
      )
    }
  }

  return (
    <OtpForm
      email={email}
      isLoading={isVerifying || isResending || isRefreshing}
      onSubmit={(otp) => void handleSubmit(otp)}
      onResend={() => void handleResend()}
    />
  )
}
